import { Phone, Printer, Mail, MapPin, Clock, ShieldCheck, ExternalLink } from "lucide-react";
import { CONTACT, HOURS } from "../data/site";
import Reveal from "../components/Reveal";
import { PageHero } from "../components/Sections";

const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&output=embed`;

const DETAIL_ICON_SIZE = 22;

const DetailItem = ({ icon: Icon, label, testId, children }) => (
  <div data-testid={testId} className="flex items-start gap-4">
    <span className="flex items-center justify-center w-12 h-12 rounded-sm bg-tiffany/10 shrink-0">
      <Icon className="text-tiffany" size={DETAIL_ICON_SIZE} />
    </span>
    <div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">{label}</p>
      {children}
    </div>
  </div>
);

const ContactDetails = () => (
  <Reveal className="lg:col-span-2">
    <div className="space-y-6">
      <DetailItem icon={Phone} label="Call Us" testId="contact-phone">
        <a href={CONTACT.phoneHref} className="text-xl font-semibold text-navy hover:text-tiffany transition-colors">
          {CONTACT.phone}
        </a>
      </DetailItem>

      <DetailItem icon={Printer} label="Fax" testId="contact-fax">
        <p className="text-lg font-medium text-navy">{CONTACT.fax}</p>
      </DetailItem>

      <DetailItem icon={Mail} label="Email" testId="contact-email">
        <a href={CONTACT.emailHref} className="text-base font-medium text-navy hover:text-tiffany transition-colors break-all">
          {CONTACT.email}
        </a>
      </DetailItem>

      <DetailItem icon={MapPin} label="Visit Us" testId="contact-address">
        <p className="text-base font-medium text-navy">{CONTACT.addressLine1}</p>
        <p className="text-base font-medium text-navy">{CONTACT.addressLine2}</p>
        <a
          href={CONTACT.directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-directions-link"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-tiffany hover:text-tiffany-dark transition-colors mt-2"
        >
          Get Directions <ExternalLink size={14} />
        </a>
      </DetailItem>

      <a
        href={CONTACT.phoneHref}
        data-testid="contact-call-button"
        className="inline-flex items-center gap-3 bg-tiffany hover:bg-tiffany-dark text-white px-7 py-4 rounded-sm font-semibold transition-colors active:scale-[0.98]"
      >
        <Phone size={18} /> Call {CONTACT.phone}
      </a>
    </div>
  </Reveal>
);

const HoursAndMap = () => (
  <Reveal delay={0.12} className="lg:col-span-3">
    <div className="space-y-6">
      <div data-testid="contact-hours" className="bg-muted rounded-sm border border-border p-7">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="text-tiffany" size={20} />
          <h3 className="font-serif text-2xl font-semibold text-navy">Office Hours</h3>
        </div>
        <ul className="divide-y divide-border">
          {HOURS.map((h) => (
            <li key={h.day} className="flex justify-between py-2.5 text-sm">
              <span className="text-foreground font-medium">{h.day}</span>
              <span className="text-muted-foreground">{h.time}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-sm overflow-hidden border border-border h-[340px]">
        <iframe
          title="Brodheadsville Eye Care location"
          data-testid="contact-map"
          src={mapSrc}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  </Reveal>
);

const InsuranceSection = () => (
  <section data-testid="insurances-section" className="bg-navy text-white py-24 sm:py-28">
    <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
      <Reveal>
        <span className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-tiffany/20 mb-6">
          <ShieldCheck className="text-tiffany" size={28} />
        </span>
        <p className="text-tiffany uppercase tracking-[0.3em] text-xs font-semibold mb-4">
          Insurance
        </p>
        <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight">
          We accept most vision &amp; medical insurance plans
        </h2>
        <p className="text-white/60 text-base mt-5">
          Don't see or aren't sure about your plan? Give us a call at{" "}
          <a href={CONTACT.phoneHref} className="text-tiffany hover:underline">{CONTACT.phone}</a>{" "}
          and we'll be happy to check your coverage for you.
        </p>
      </Reveal>
    </div>
  </section>
);

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact Us"
        title="We'd love to see you"
        subtitle="Call us to schedule a visit or stop by our office in Brodheadsville. We accept most vision and medical insurance plans."
      />
      <section className="py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-10">
          <ContactDetails />
          <HoursAndMap />
        </div>
      </section>
      <InsuranceSection />
    </div>
  );
}
